import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  XCircle, 
  Clock, 
  MessageSquare, 
  Building2, 
  User, 
  Briefcase, 
  Code, 
  CheckCircle, 
  Rocket,
  Mail,
  MapPin,
  FileText
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { companyInfo } from '../mockData';

const iconMap = {
  calendar: Calendar,
  'x-circle': XCircle,
  clock: Clock,
  'message-square': MessageSquare,
  'building-2': Building2,
  user: User,
  briefcase: Briefcase,
  code: Code,
  'check-circle': CheckCircle,
  rocket: Rocket
};

const LandingPage = () => {
  const [activeSection, setActiveSection] = useState('about');

  useEffect(() => {
    const handleScroll = () => {
      const sections = companyInfo.navigation.map(nav => nav.id);
      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offsetTop = element.offsetTop - 80;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Sticky Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-slate-900">{companyInfo.name}</h1>
            </div>
            <nav className="hidden md:flex items-center space-x-6">
              {companyInfo.navigation.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-sm font-medium transition-colors hover:text-blue-600 ${
                    activeSection === item.id ? 'text-blue-600' : 'text-slate-600'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="about" className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              {companyInfo.introduction.title}
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              {companyInfo.introduction.description}
            </p>
            <div className="mt-8 inline-flex items-center px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
              {companyInfo.tagline}
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section id="what-we-do" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {companyInfo.whatWeDo.title}
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                {companyInfo.whatWeDo.description}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {companyInfo.whatWeDo.services.map((service) => {
                const IconComponent = iconMap[service.icon];
                return (
                  <Card key={service.id} className="border-slate-200 hover:border-blue-300 transition-colors">
                    <CardHeader>
                      <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                        <IconComponent className="w-6 h-6 text-blue-600" />
                      </div>
                      <CardTitle className="text-xl text-slate-900">{service.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-slate-600 text-base">
                        {service.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section id="who-its-for" className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {companyInfo.whoItsFor.title}
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                {companyInfo.whoItsFor.description}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {companyInfo.whoItsFor.targetAudience.map((audience) => {
                const IconComponent = iconMap[audience.icon];
                return (
                  <Card key={audience.id} className="bg-white border-slate-200">
                    <CardHeader>
                      <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-4">
                        <IconComponent className="w-6 h-6 text-slate-700" />
                      </div>
                      <CardTitle className="text-xl text-slate-900">{audience.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-slate-600 text-base">
                        {audience.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Current Status Section */}
      <section id="status" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {companyInfo.currentStatus.title}
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                {companyInfo.currentStatus.description}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {companyInfo.currentStatus.statusItems.map((item) => {
                const IconComponent = iconMap[item.icon];
                return (
                  <Card key={item.id} className="border-slate-200 bg-slate-50">
                    <CardHeader>
                      <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mb-4 shadow-sm">
                        <IconComponent className="w-6 h-6 text-blue-600" />
                      </div>
                      <CardTitle className="text-xl text-slate-900">{item.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-slate-600 text-base">
                        {item.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Contact & Company Details Section */}
      <section id="contact" className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Company Details
              </h2>
              <p className="text-lg text-slate-600">
                Get in touch or find out more about us
              </p>
            </div>
            <Card className="bg-white border-slate-200">
              <CardContent className="p-8">
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Registered Company</h3>
                      <p className="text-slate-600">{companyInfo.name}</p>
                      <p className="text-slate-500 text-sm mt-1">Company Number: {companyInfo.registeredNumber}</p>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Registered Address</h3>
                      <p className="text-slate-600">{companyInfo.registeredAddress}</p>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Contact Email</h3>
                      <a 
                        href={`mailto:${companyInfo.email}`}
                        className="text-blue-600 hover:text-blue-700 transition-colors"
                      >
                        {companyInfo.email}
                      </a>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-white border-t border-slate-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-slate-600 text-sm">
              © {new Date().getFullYear()} {companyInfo.name}. All rights reserved.
            </p>
            <p className="text-slate-500 text-xs mt-2">
              Registered in England and Wales. Company Number: {companyInfo.registeredNumber}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
